import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'

interface HeroCta {
  label: string
  to: string
}

interface HeroSectionProps {
  imageSrc: string
  title: string
  subtitle?: string
  slogan?: string
  className?: string
  imagePosition?: string
  imageFit?: 'cover' | 'contain'
  fullScreen?: boolean
  blurEdges?: boolean
  sloganPosition?: 'top' | 'bottom'
  overlayClassName?: string
  primaryCta?: HeroCta
  secondaryCta?: HeroCta
}

export function HeroSection({
  imageSrc,
  title,
  subtitle,
  slogan,
  className,
  imagePosition = 'center',
  imageFit = 'cover',
  fullScreen = false,
  blurEdges = false,
  sloganPosition = 'top',
  overlayClassName = 'bg-brand-dark/60',
  primaryCta,
  secondaryCta,
}: HeroSectionProps) {
  const sloganNode = slogan ? (
    <p
      className={cn(
        'text-brand-red font-semibold tracking-widest text-sm md:text-base',
        sloganPosition === 'top' ? 'mb-4' : 'mt-6'
      )}
    >
      {slogan}
    </p>
  ) : null
  const ctas = (primaryCta || secondaryCta) ? (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
      {primaryCta && (
        <LocalizedLink
          to={primaryCta.to}
          className="group inline-flex items-center gap-2 bg-brand-red text-white px-8 py-3 rounded-lg font-medium hover:bg-brand-red-dark transition-colors"
        >
          {primaryCta.label}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </LocalizedLink>
      )}
      {secondaryCta && (
        <LocalizedLink
          to={secondaryCta.to}
          className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
        >
          {secondaryCta.label}
        </LocalizedLink>
      )}
    </div>
  ) : null

  if (imageFit === 'contain') {
    return (
      <section className={cn('relative w-full overflow-hidden bg-brand-dark', className)}>
        {blurEdges && (
          <div
            className="absolute inset-0 bg-cover bg-no-repeat bg-center"
            style={{
              backgroundImage: `url(${imageSrc})`,
              filter: 'blur(60px)',
              transform: 'scale(1.2)',
            }}
          />
        )}
        <img src={imageSrc} alt="" className="relative block mx-auto h-auto max-h-[80vh] w-auto max-w-full" />
        <div className={cn('absolute inset-0', overlayClassName)} />
        <div className="absolute inset-0 z-10 flex items-center justify-center px-4 sm:px-6 lg:px-8 text-center">
          <div className="mx-auto max-w-7xl">
            {sloganPosition === 'top' && sloganNode}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}
            {sloganPosition === 'bottom' && sloganNode}
            {ctas}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      className={cn(
        'relative flex items-center justify-center overflow-hidden',
        fullScreen
          ? 'min-h-screen'
          : 'min-h-[60vh] md:min-h-[70vh]',
        className
      )}
    >
      {blurEdges && (
        <div
          className="absolute inset-0 bg-cover bg-no-repeat scale-110"
          style={{
            backgroundImage: `url(${imageSrc})`,
            backgroundPosition: imagePosition,
            filter: 'blur(24px)',
          }}
        />
      )}
      <div
        className="absolute inset-0 bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url(${imageSrc})`,
          backgroundPosition: imagePosition,
          ...(blurEdges
            ? {
                WebkitMaskImage:
                  'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 100%)',
                maskImage:
                  'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 100%)',
              }
            : {}),
        }}
      />
      <div className={cn('absolute inset-0', overlayClassName)} />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        {sloganPosition === 'top' && sloganNode}
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            {subtitle}
          </p>
        )}
        {sloganPosition === 'bottom' && sloganNode}
        {ctas}
      </div>
    </section>
  )
}
